/**
 * RecentChatList — Chatwoot conversations for dashboard right column
 *
 * Three states:
 * 1. No Chatwoot account → marketing CTA (2 free channels)
 * 2. Has account, no conversations → friendly empty state
 * 3. Has conversations → conversation list with channel icons
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, Globe, Mail, MessageCircle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { getCPToken } from "@/lib/api/controlPlaneClient";
import { useChatwootPulse } from "@/hooks/useChatwootPulse";
import { CARD } from "@/config/design-tokens";
import { usePreferencesStore } from "@/lib/store/preferencesStore";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

// ── Types ──

interface Conversation {
  id: number;
  contact_name: string;
  contact_avatar: string;
  last_message: string;
  timestamp: string;
  inbox_name: string;
  unread_count: number;
  status: string;
  channel: string;
  assignee_name: string;
  assignee_avatar: string;
}

interface ConversationsResponse {
  success: boolean;
  account_id?: string;
  conversations: Conversation[];
}

// ── Channel Icons (matching Chatwoot's channel types) ──

function LineIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 10.304c0-5.369-5.383-9.738-12-9.738S0 4.935 0 10.304c0 4.814 4.27 8.846 10.035 9.608.391.084.922.258 1.057.592.121.303.079.778.039 1.084l-.171 1.027c-.053.303-.242 1.186 1.039.647 1.281-.54 6.911-4.069 9.428-6.967C23.257 14.228 24 12.39 24 10.304zm-16.86 2.676a.348.348 0 01-.348.349H3.93a.348.348 0 01-.348-.349V8.478a.348.348 0 01.348-.348h.578a.348.348 0 01.348.348v3.924h1.936a.348.348 0 01.348.349v.577zm1.894-.001a.348.348 0 01-.348.349h-.578a.348.348 0 01-.348-.349V8.478a.348.348 0 01.348-.348h.578a.348.348 0 01.348.348v4.501zm5.604 0a.348.348 0 01-.348.349h-.578a.348.348 0 01-.31-.189l-1.969-2.661v2.502a.348.348 0 01-.348.348h-.578a.348.348 0 01-.348-.348V8.478a.348.348 0 01.348-.348h.578c.028 0 .054.003.08.01a.348.348 0 01.228.178l1.973 2.666V8.478a.348.348 0 01.348-.348h.578a.348.348 0 01.348.348v4.501zm3.476-3.924a.348.348 0 01-.348.349h-1.936v.924h1.936a.348.348 0 01.348.348v.578a.348.348 0 01-.348.348h-1.936v.925h1.936a.348.348 0 01.348.348v.578a.348.348 0 01-.348.349h-2.862a.348.348 0 01-.348-.349V8.478a.348.348 0 01.348-.348h2.862a.348.348 0 01.348.348v.577z" />
    </svg>
  );
}

function MessengerIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.975 12-11.111S18.627 0 12 0zm1.193 14.963l-3.056-3.26-5.963 3.26L10.732 8.2l3.131 3.26 5.889-3.26-6.559 6.763z" />
    </svg>
  );
}

function InstagramIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function WhatsAppIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function TelegramIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function SmsIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
    </svg>
  );
}

function ApiIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function ChannelIcon({ channel }: { channel: string }) {
  const iconSize = 10;

  switch (channel) {
    case "Channel::Line":
      return (
        <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#06C755" }}>
          <span className="text-white"><LineIcon size={iconSize} /></span>
        </span>
      );
    case "Channel::FacebookPage":
      return (
        <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#0084FF" }}>
          <span className="text-white"><MessengerIcon size={iconSize} /></span>
        </span>
      );
    case "Channel::Instagram":
      return (
        <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}>
          <span className="text-white"><InstagramIcon size={iconSize} /></span>
        </span>
      );
    case "Channel::Whatsapp":
      return (
        <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#25D366" }}>
          <span className="text-white"><WhatsAppIcon size={iconSize} /></span>
        </span>
      );
    case "Channel::Telegram":
      return (
        <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#0088CC" }}>
          <span className="text-white"><TelegramIcon size={iconSize} /></span>
        </span>
      );
    case "Channel::Email":
      return (
        <span className="w-4 h-4 rounded bg-gray-700 dark:bg-gray-500 flex items-center justify-center flex-shrink-0">
          <Mail className="w-2.5 h-2.5 text-white" />
        </span>
      );
    case "Channel::WebWidget":
      return (
        <span className="w-4 h-4 rounded bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Globe className="w-2.5 h-2.5 text-white" />
        </span>
      );
    case "Channel::Sms":
      return (
        <span className="w-4 h-4 rounded bg-green-700 flex items-center justify-center flex-shrink-0">
          <span className="text-white"><SmsIcon size={iconSize} /></span>
        </span>
      );
    case "Channel::Api":
      return (
        <span className="w-4 h-4 rounded bg-purple-600 flex items-center justify-center flex-shrink-0">
          <span className="text-white"><ApiIcon size={iconSize} /></span>
        </span>
      );
    default:
      return (
        <span className="w-4 h-4 rounded bg-gray-800 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
          <MessageSquare className="w-2.5 h-2.5 text-white" />
        </span>
      );
  }
}

// ── Chatwoot conversations hook ──

function useRecentConversations() {
  const tenant = useAuthStore((s) => s.currentTenant);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["dashboard-recent-conversations", tenant],
    queryFn: async (): Promise<ConversationsResponse> => {
      const token = getCPToken();
      if (!token) return { success: true, conversations: [] };

      const resp = await fetch(
        `${CONTROL_PLANE_URL}/api/method/control_plane.api.chatwoot.get_recent_conversations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          body: JSON.stringify({
            access_token: token,
            tenant_name: tenant,
          }),
        }
      );

      if (!resp.ok) return { success: true, conversations: [] };
      const data = await resp.json();
      return (data.message as ConversationsResponse) ?? { success: true, conversations: [] };
    },
    enabled: isAuthenticated && !!tenant,
    staleTime: 60_000,
  });
}

/** Chatwoot-style timestamp: "21d . 1h", "5h . 2m" */
function formatChatTime(timestamp?: string): string {
  if (!timestamp) return "";
  const d = new Date(typeof timestamp === "number" ? timestamp * 1000 : timestamp);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;

  if (diffDays >= 1) {
    const remainingHours = diffHours - diffDays * 24;
    return `${diffDays}d \u2022 ${remainingHours}h`;
  }

  const remainingMins = diffMins - diffHours * 60;
  return `${diffHours}h \u2022 ${remainingMins}m`;
}

function SkeletonRows() {
  return (
    <div className="space-y-0">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <div className="h-9 w-9 bg-muted rounded-full animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-24 bg-muted rounded animate-pulse" />
            <div className="h-3 w-32 bg-muted rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── State: No Chatwoot account — marketing CTA ──

function NoAccountCTA() {
  return (
    <div className="px-6 pb-6 pt-2">
      <div className="flex flex-col items-center text-center py-6">
        {/* Channel icon badges */}
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#06C755" }}>
            <span className="text-white"><LineIcon size={16} /></span>
          </span>
          <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#0084FF" }}>
            <span className="text-white"><MessengerIcon size={16} /></span>
          </span>
          <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}>
            <span className="text-white"><InstagramIcon size={16} /></span>
          </span>
        </div>

        <p className="text-sm font-medium text-foreground">
          {"\u0E23\u0E27\u0E21\u0E41\u0E0A\u0E17\u0E25\u0E39\u0E01\u0E04\u0E49\u0E32\u0E44\u0E27\u0E49\u0E17\u0E35\u0E48\u0E40\u0E14\u0E35\u0E22\u0E27"}
        </p>
        <p className="text-xs text-muted-foreground mt-1 max-w-[220px]">
          {"\u0E40\u0E0A\u0E37\u0E48\u0E2D\u0E21\u0E15\u0E48\u0E2D LINE, Facebook, Instagram \u0E41\u0E25\u0E30\u0E2D\u0E37\u0E48\u0E19\u0E46 \u0E1F\u0E23\u0E35 2 \u0E0A\u0E48\u0E2D\u0E07\u0E17\u0E32\u0E07"}
        </p>

        <Link
          to="/inbox"
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {"\u0E40\u0E23\u0E34\u0E48\u0E21\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19\u0E1F\u0E23\u0E35"}
        </Link>
      </div>
    </div>
  );
}

// ── State: Has account but no conversations ──

function EmptyConversations() {
  return (
    <div className="px-6 pb-6 pt-2">
      <div className="flex flex-col items-center text-center py-6">
        <MessageCircle className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-foreground">
          {"\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E01\u0E32\u0E23\u0E2A\u0E19\u0E17\u0E19\u0E32"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {"\u0E40\u0E21\u0E37\u0E48\u0E2D\u0E25\u0E39\u0E01\u0E04\u0E49\u0E32\u0E17\u0E31\u0E01\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21\u0E08\u0E30\u0E41\u0E2A\u0E14\u0E07\u0E17\u0E35\u0E48\u0E19\u0E35\u0E48"}
        </p>
      </div>
    </div>
  );
}

// ── Main Export ──

export function RecentChatList() {
  const { data: chatwootPulse, isFetched: pulseFetched } = useChatwootPulse();
  const hasAccount = chatwootPulse?.has_chatwoot_account ?? false;
  const chatwootStateOverride = usePreferencesStore((s) => s.chatwootStateOverride);

  const { data, isLoading: isConvosLoading } = useRecentConversations();
  const conversations = data?.conversations ?? [];
  const accountId = data?.account_id;

  // Dev override: force a specific state
  if (chatwootStateOverride === 'no_account') {
    return (
      <div className={cn(CARD, "shadow-none")}>
        <div className="flex items-center justify-between px-6 pt-6 pb-0">
          <h3 className="text-sm font-semibold text-foreground">
            {"\u0E01\u0E25\u0E48\u0E2D\u0E07\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21"}
          </h3>
        </div>
        <NoAccountCTA />
      </div>
    );
  }
  if (chatwootStateOverride === 'empty') {
    return (
      <div className={cn(CARD, "shadow-none")}>
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h3 className="text-sm font-semibold text-foreground">
            {"\u0E41\u0E0A\u0E17"}
          </h3>
          <Link to="/inbox" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            {"\u0E40\u0E1B\u0E34\u0E14\u0E01\u0E25\u0E48\u0E2D\u0E07\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21"}
          </Link>
        </div>
        <EmptyConversations />
      </div>
    );
  }
  // 'has_conversations' override: skip loading/empty checks, show whatever data we have
  // (falls through to normal rendering below, but skips the !hasAccount gate)

  // Wait for pulse data before deciding what to show
  if (chatwootStateOverride === 'auto' && !pulseFetched) {
    return (
      <div className={cn(CARD, "shadow-none")}>
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h3 className="text-sm font-semibold text-foreground">
            {"\u0E41\u0E0A\u0E17"}
          </h3>
        </div>
        <div className="px-2 pb-4">
          <SkeletonRows />
        </div>
      </div>
    );
  }

  // State 1: No Chatwoot account — marketing CTA
  if (chatwootStateOverride === 'auto' && !hasAccount) {
    return (
      <div className={cn(CARD, "shadow-none")}>
        <div className="flex items-center justify-between px-6 pt-6 pb-0">
          <h3 className="text-sm font-semibold text-foreground">
            {"\u0E01\u0E25\u0E48\u0E2D\u0E07\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21"}
          </h3>
        </div>
        <NoAccountCTA />
      </div>
    );
  }

  // State 2 & 3: Has account — show conversations or empty state
  return (
    <div className={cn(CARD, "shadow-none")}>
      <div className="flex items-center justify-between px-6 pt-6 pb-2">
        <h3 className="text-sm font-semibold text-foreground">
          {"\u0E41\u0E0A\u0E17"}
        </h3>
        <Link
          to="/inbox"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {"\u0E40\u0E1B\u0E34\u0E14\u0E01\u0E25\u0E48\u0E2D\u0E07\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21"}
        </Link>
      </div>

      {isConvosLoading ? (
        <div className="px-2 pb-4">
          <SkeletonRows />
        </div>
      ) : !conversations.length ? (
        // State 2: Has account but no conversations
        <EmptyConversations />
      ) : (
        // State 3: Conversations list
        <div className="pb-2">
          {conversations.map((convo, i) => (
            <div key={convo.id}>
              <Link
                to={accountId
                  ? `/inbox/accounts/${accountId}/conversations/${convo.id}`
                  : `/inbox`
                }
                className="flex gap-3 px-4 py-3 mx-2 rounded-lg hover:bg-accent/50 transition-colors"
              >
                {/* Contact avatar */}
                {convo.contact_avatar ? (
                  <img
                    src={convo.contact_avatar}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-muted mt-3"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center flex-shrink-0 mt-3">
                    <span className="text-sm font-medium text-muted-foreground">
                      {(convo.contact_name || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Row 1: channel icon + inbox name --- timestamp */}
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <ChannelIcon channel={convo.channel} />
                      <span className="text-xs text-muted-foreground truncate">
                        {convo.inbox_name || "Inbox"}
                      </span>
                    </div>
                    <span className={cn(
                      "text-xs flex-shrink-0 ml-2",
                      convo.unread_count > 0 ? "text-foreground font-medium" : "text-muted-foreground"
                    )}>
                      {formatChatTime(convo.timestamp)}
                    </span>
                  </div>
                  {/* Row 2: contact name */}
                  <span className={cn(
                    "text-sm truncate block",
                    convo.unread_count > 0 ? "font-semibold text-foreground" : "font-medium text-foreground"
                  )}>
                    {convo.contact_name || "\u0E44\u0E21\u0E48\u0E17\u0E23\u0E32\u0E1A\u0E0A\u0E37\u0E48\u0E2D"}
                  </span>
                  {/* Row 3: last message preview */}
                  <p className={cn(
                    "text-xs truncate mt-0.5",
                    convo.unread_count > 0 ? "text-foreground/70" : "text-muted-foreground"
                  )}>
                    {convo.last_message || "\u2014"}
                  </p>
                </div>
                {/* Unread badge — far right, primary blue */}
                {convo.unread_count > 0 && (
                  <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center self-center">
                    {convo.unread_count > 99 ? "99+" : convo.unread_count}
                  </span>
                )}
              </Link>
              {i < conversations.length - 1 && (
                <div className="mx-6 border-t border-border/30" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
